import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Card, CardContent,
    useTheme, useMediaQuery, Chip, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, LinearProgress, Tooltip, Stack
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DnsIcon from '@mui/icons-material/Dns';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LanguageIcon from '@mui/icons-material/Language';

import api from '../services/api';

const VERDE = '#1ABB9C';

const fmtBytes = (bytes) => {
    if (bytes == null || isNaN(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
};

const fmtPct = (n) => (n == null || isNaN(n)) ? '—' : `${(n * 100).toFixed(1)}%`;
const fmtNum = (n) => (n == null || isNaN(n)) ? '—' : n.toLocaleString('pt-BR');

const fmtUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const parts = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}min`);
    if (s && !d) parts.push(`${s}s`);
    return parts.length ? parts.join(' ') : 'recém iniciado';
};

const corPctUso = (pct) => {
    if (pct == null) return 'primary';
    if (pct >= 0.9) return 'error';
    if (pct >= 0.7) return 'warning';
    return 'success';
};

function MetricCard({ icon, titulo, valor, sub, progresso, progressoCor }) {
    return (
        <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    {icon}
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {titulo}
                    </Typography>
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
                    {valor}
                </Typography>
                {sub && (
                    <Typography variant="caption" color="text.secondary">
                        {sub}
                    </Typography>
                )}
                {progresso != null && (
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, progresso * 100))}
                        color={progressoCor || 'primary'}
                        sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
                    />
                )}
            </CardContent>
        </Card>
    );
}

function DetalhesComponente({ details }) {
    if (!details || typeof details !== 'object') return <Typography variant="caption" color="text.secondary">—</Typography>;
    const entries = Object.entries(details);
    if (entries.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
    return (
        <Stack spacing={0.3}>
            {entries.map(([k, v]) => {
                let display = v;
                if (typeof v === 'number' && k.toLowerCase().match(/total|free|threshold|used/)) {
                    display = fmtBytes(v);
                } else if (typeof v === 'object') {
                    display = JSON.stringify(v);
                }
                return (
                    <Typography key={k} variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        <strong>{k}:</strong> {String(display)}
                    </Typography>
                );
            })}
        </Stack>
    );
}

export default function StatusSistema() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [healthData, setHealthData] = useState(null);
    const [metrics, setMetrics] = useState({});
    const [ultimaAtt, setUltimaAtt] = useState(null);
    const [error, setError] = useState(false);

    const fetchMetric = async (nome) => {
        try {
            const r = await api.get(`/actuator/metrics/${nome}`);
            return r.data?.measurements?.[0]?.value ?? null;
        } catch {
            return null;
        }
    };

    const carregarStatus = async () => {
        setLoading(true);
        setError(false);
        try {
            const [health, uptime, memUsed, memMax, cpuSys, cpuProc, threads, classes] = await Promise.all([
                api.get('/actuator/health').then(r => r.data).catch(e => ({ status: 'DOWN', error: e.message })),
                fetchMetric('process.uptime'),
                fetchMetric('jvm.memory.used'),
                fetchMetric('jvm.memory.max'),
                fetchMetric('system.cpu.usage'),
                fetchMetric('process.cpu.usage'),
                fetchMetric('jvm.threads.live'),
                fetchMetric('jvm.classes.loaded')
            ]);

            setHealthData(health);
            setMetrics({ uptime, memUsed, memMax, cpuSys, cpuProc, threads, classes });
            setUltimaAtt(new Date());
        } catch (err) {
            console.error('Erro ao carregar status:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarStatus();
        const interval = setInterval(carregarStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading && !healthData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: VERDE }} />
            </Box>
        );
    }

    const isSystemUp = healthData?.status === 'UP';
    const disco = healthData?.components?.diskSpace?.details;
    const discoUsado = disco ? (disco.total - disco.free) : null;
    const discoPct = disco ? (disco.total - disco.free) / disco.total : null;
    const memPct = (metrics.memUsed && metrics.memMax) ? metrics.memUsed / metrics.memMax : null;
    const ping = healthData?.components?.googlePing;
    const pingUp = ping?.status === 'UP';
    const pingLatencia = ping?.details?.latency_ms;

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Status do Sistema
                    </Typography>
                    {ultimaAtt && (
                        <Typography variant="caption" color="text.secondary">
                            Atualizado em {ultimaAtt.toLocaleTimeString('pt-BR')}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                        icon={isSystemUp ? <CheckCircleIcon /> : <ErrorIcon />}
                        label={isSystemUp ? 'Operacional' : 'Fora do ar'}
                        color={isSystemUp ? 'success' : 'error'}
                        sx={{ fontWeight: 600 }}
                    />
                    <Tooltip title="Atualizar agora">
                        <IconButton onClick={carregarStatus} disabled={loading} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Falha ao conectar com o Actuator. Verifique se /api/actuator está acessível.
                </Alert>
            )}

            {/* Grid de métricas */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<AccessTimeIcon sx={{ color: VERDE }} />}
                        titulo="Uptime"
                        valor={fmtUptime(metrics.uptime)}
                        sub="Tempo desde último restart"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<MemoryIcon sx={{ color: VERDE }} />}
                        titulo="Memória JVM"
                        valor={fmtBytes(metrics.memUsed)}
                        sub={`de ${fmtBytes(metrics.memMax)} (${fmtPct(memPct)})`}
                        progresso={memPct}
                        progressoCor={corPctUso(memPct)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<StorageIcon sx={{ color: VERDE }} />}
                        titulo="Disco"
                        valor={fmtBytes(discoUsado)}
                        sub={disco ? `de ${fmtBytes(disco.total)} (${fmtPct(discoPct)})` : '—'}
                        progresso={discoPct}
                        progressoCor={corPctUso(discoPct)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<SpeedIcon sx={{ color: VERDE }} />}
                        titulo="CPU"
                        valor={fmtPct(metrics.cpuProc)}
                        sub={`Sistema: ${fmtPct(metrics.cpuSys)}`}
                        progresso={metrics.cpuProc}
                        progressoCor={corPctUso(metrics.cpuProc)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<AccountTreeIcon sx={{ color: VERDE }} />}
                        titulo="Threads ativas"
                        valor={fmtNum(metrics.threads)}
                        sub="JVM"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<DnsIcon sx={{ color: VERDE }} />}
                        titulo="Classes carregadas"
                        valor={fmtNum(metrics.classes)}
                        sub="JVM"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        icon={<LanguageIcon sx={{ color: pingUp ? VERDE : '#f44336' }} />}
                        titulo="Internet (Google)"
                        valor={pingUp ? `${pingLatencia ?? '—'} ms` : 'Offline'}
                        sub={pingUp ? 'Conexão externa OK' : 'Sem acesso à internet'}
                    />
                </Grid>
            </Grid>

            {/* Tabela componentes */}
            {healthData?.components && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Componentes do sistema
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 700, width: '20%' }}>Componente</TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: '15%' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Detalhes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(healthData.components).map(([key, value]) => (
                                    <TableRow key={key} hover>
                                        <TableCell sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                            {key}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={value.status}
                                                color={value.status === 'UP' ? 'success' : 'error'}
                                                size="small"
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <DetalhesComponente details={value.details} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
